import { Target_CreateCartInput } from "client/graphql/commerce-v2-api"
import {
  createContext,
  ReactNode,
  Reducer,
  useContext,
  useReducer,
} from "react"

export const MAX_QUANTITY = 10 // Default 10 is in alignment with Target.com

export type TargetCartItem = {
  productId: string
  variantId: string
  price: string
  quantity: number
}

export type TargetCartContents = Record<string, TargetCartItem>

type TargetCartState = {
  contents: TargetCartContents
}

type TargetCartAction =
  | {
      type: "ADD_TO_CART"
      payload: { cartItem: TargetCartItem }
    }
  | {
      type: "REMOVE_FROM_CART"
      payload: { variantId: string }
    }
  | {
      type: "UPDATE_QUANTITY"
      payload: {
        variantId: string
        quantity: number
      }
    }

type TargetCartReducer = Reducer<TargetCartState, TargetCartAction>

type TargetCartValue = TargetCartState & {
  addToCart: (cartItem: TargetCartItem) => void
  removeFromCart: (variantId: string) => void
  updateQuantity: (variantId: string, quantity: number) => void
}

type TargetCartProviderProps = {
  children?: ReactNode
}

export const getCartTotalItemCount = (
  cartContents: TargetCartContents,
) => {
  return Object.values(cartContents).reduce(
    (qty, item) => qty + item.quantity,
    0,
  )
}

export const formatForCartCreation = (
  cartContents: TargetCartContents,
): Target_CreateCartInput[] => {
  return Object.values(cartContents).map((cartItem) => {
    return {
      tcin: cartItem.variantId,
      quantity: cartItem.quantity,
    }
  })
}

export const getCartTotalProductCount = (
  cartContents: TargetCartContents,
) => {
  return Object.keys(cartContents).length
}

export const getTotalCartCost = (cartContents: TargetCartContents) => {
  return Object.values(cartContents).reduce((sum, cartItem) => {
    return sum + parseFloat(cartItem.price) * cartItem.quantity
  }, 0)
}

const addItemToCart = (
  cartItem: TargetCartItem,
  cartContents: TargetCartContents,
): TargetCartContents => {
  if (cartContents[cartItem.variantId]) {
    return {
      ...cartContents,
      [cartItem.variantId]: {
        ...cartContents[cartItem.variantId],
        quantity: Math.min(
          cartContents[cartItem.variantId].quantity + cartItem.quantity,
          MAX_QUANTITY,
        ),
      },
    }
  }

  return {
    ...cartContents,
    [cartItem.variantId]: cartItem,
  }
}

const removeItemFromCart = (
  variantId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { [variantId]: removed, ...rest }: TargetCartContents,
) => {
  return rest
}

const updateQuantity = (
  variantId: string,
  quantity: number,
  cartContents: TargetCartContents,
) => {
  return {
    ...cartContents,
    [variantId]: {
      ...cartContents[variantId],
      quantity,
    },
  }
}

const cartReducer: TargetCartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_TO_CART":
      return {
        ...state,
        contents: addItemToCart(action.payload.cartItem, state.contents),
      }
    case "REMOVE_FROM_CART":
      return {
        ...state,
        contents: removeItemFromCart(
          action.payload.variantId,
          state.contents,
        ),
      }
    case "UPDATE_QUANTITY":
      return {
        ...state,
        contents: updateQuantity(
          action.payload.variantId,
          action.payload.quantity,
          state.contents,
        ),
      }
    default:
      return state
  }
}

const TargetCartContext = createContext<TargetCartValue | undefined>(
  undefined,
)

export const TargetCartProvider = ({
  children,
}: TargetCartProviderProps) => {
  const [{ contents }, dispatch] = useReducer(cartReducer, {
    contents: {},
  })

  return (
    <TargetCartContext.Provider
      value={{
        addToCart(cartItem: TargetCartItem) {
          dispatch({
            type: "ADD_TO_CART",
            payload: { cartItem },
          })
        },
        removeFromCart(variantId: string) {
          dispatch({
            type: "REMOVE_FROM_CART",
            payload: { variantId },
          })
        },
        updateQuantity(variantId: string, quantity: number) {
          if (quantity <= 0) {
            dispatch({
              type: "REMOVE_FROM_CART",
              payload: { variantId },
            })
          } else {
            dispatch({
              type: "UPDATE_QUANTITY",
              payload: { variantId, quantity },
            })
          }
        },
        contents,
      }}
    >
      {children}
    </TargetCartContext.Provider>
  )
}

export const useTargetCartContext = () => {
  const targetCartContext = useContext(TargetCartContext)
  if (!targetCartContext) {
    throw new Error(
      "No TargetCartContext.Provider found when calling useTargetCartContext.",
    )
  }
  return targetCartContext
}
